import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";

const SCROLL_THRESHOLD = 72;
const SCROLL_DELTA = 8;

const Navbar: React.FC = () => {
  const { t, lang, dir, toggleLanguage } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const lastScroll = useRef<number>(0);
  const location = useLocation();
  const isRtl = dir === "rtl";
  const forceSolidState = location.pathname === "/contact";

  const links = [
    { to: "/", label: t?.nav?.home || "Home" },
    { to: "/services", label: t?.nav?.services || "Services" },
   // { to: "/blog", label: t?.nav?.blog || "Blogs" },
    { to: "/about", label: t?.nav?.about || "About us" },
  ];

  const ctaLabel = lang === "ar" ? t?.nav?.contact || "تواصل معنا" : "Get in touch";
  const languageLabel = lang === "en" ? "العربية" : "English";

  useEffect(() => {
    const onScroll = () => {
      const current = window.scrollY;
      const delta = current - lastScroll.current;

      setHasScrolled(current > 12);

      if (menuOpen || current <= SCROLL_THRESHOLD) {
        setVisible(true);
        lastScroll.current = current;
        return;
      }

      if (Math.abs(delta) >= SCROLL_DELTA) {
        setVisible(delta < 0);
        lastScroll.current = current;
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const handleLanguageToggle = () => {
    toggleLanguage();
    closeMenu();
  };

  return (
    <nav
      dir={isRtl ? "rtl" : "ltr"}
      className={`fixed left-0 right-0 top-0 z-50 text-white transition-[transform,background-color,box-shadow,backdrop-filter] duration-300 ${
        visible ? "translate-y-0" : "-translate-y-full"
      } ${forceSolidState || hasScrolled || menuOpen ? "bg-second-color/95 shadow-lg shadow-black/10 backdrop-blur-md" : "bg-transparent"}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div
        className={`site-container flex h-[88px] items-center justify-between py-5 lg:py-6 ${
          isRtl ? "flex-row-reverse" : ""
        }`}
      >
        <Link
          to="/"
          onClick={closeMenu}
          aria-label="Eduvate home"
          className="flex h-[42px] w-[86px] shrink-0 items-center justify-center"
        >
          <img
            src="/images/kk.svg"
            alt="Eduvate"
            className="h-[42px] w-[86px]"
          />
        </Link>

        <div
          className={`hidden h-10 items-center gap-8 md:flex ${
            isRtl ? "flex-row-reverse" : ""
          }`}
        >
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `font-sans text-base font-medium leading-6 transition-colors hover:text-main-color ${
                  isActive ? "text-main-color" : "text-white"
                }`
              }
              end={link.to === "/"}
            >
              <span
                className={`flex items-center justify-center gap-2.5 ${
                  isRtl ? "flex-row-reverse" : ""
                }`}
              >
                {link.label}
              </span>
            </NavLink>
          ))}

          <Link
            to="/contact"
            data-property-1="second"
            className="flex h-10 w-36 items-center justify-center gap-2.5 bg-main-color px-6 py-2 text-center font-sans text-base font-medium leading-6 text-second-color transition-colors hover:bg-lime-300 focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2 focus:ring-offset-second-color"
          >
            {lang === "ar" ? (
              ctaLabel
            ) : (
              <span>
                <span className="capitalize">Get</span>
                <span className="lowercase"> in touch</span>
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={handleLanguageToggle}
            className="flex h-10 items-center justify-center border border-white/25 px-4 py-2 font-sans text-base font-medium leading-6 text-white transition-colors hover:border-main-color hover:text-main-color focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2 focus:ring-offset-second-color"
          >
            {languageLabel}
          </button>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          className="inline-flex h-10 w-10 items-center justify-center text-white transition-colors hover:text-main-color focus:outline-none focus:ring-2 focus:ring-main-color md:hidden"
        >
          {menuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-white/10 bg-second-color/95 backdrop-blur-md md:hidden">
          <div
            className={`site-container flex flex-col gap-1 py-4 ${
              isRtl ? "items-end text-right" : "items-start text-left"
            }`}
          >
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `py-3 font-sans text-base font-medium leading-6 transition-colors hover:text-main-color ${
                    isActive ? "text-main-color" : "text-white"
                  }`
                }
                end={link.to === "/"}
                onClick={closeMenu}
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={closeMenu}
              className="mt-2 flex h-10 w-36 items-center justify-center bg-main-color px-6 py-2 font-sans text-base font-medium leading-6 text-second-color"
            >
              {ctaLabel}
            </Link>
            <button
              type="button"
              onClick={handleLanguageToggle}
              className="mt-3 flex h-10 w-fit items-center justify-center border border-white/25 px-4 py-2 font-sans text-base font-medium leading-6 text-white transition-colors hover:border-main-color hover:text-main-color"
            >
              {languageLabel}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
